import styled from 'styled-components';

import { Logo as LogoElement } from '~/elements/Logo';

export const Logo = styled(LogoElement)`
  margin: auto;
`;

// Where the logo flies to: the app shell sidebar, or the public header shown over onboarding.
export type LogoTarget = 'app' | 'onboarding';

const LOGO_TARGET_SELECTOR: Record<LogoTarget, string> = {
  app: '[data-logo-target]',
  onboarding: '[data-onboarding-logo-target]',
};

const DESKTOP_LOGO_TARGET = { x: 24, y: 18, scale: 16 / 24 };
const MOBILE_LOGO_TARGET = { x: 12, y: 19, scale: 14 / 24 };
const FINAL_LETTER_OFFSET = { x: 31, y: 7 };
const LOGO_ANIMATION_EASE = 'power1.out';
const PANEL_EXIT_DURATION = 0.35;

export const animateLogo = (
  logo: SVGElement,
  logoWrapper: HTMLDivElement,
  panel: HTMLDivElement,
  gs: any,
  pathPlugin: any,
  callback: () => void,
  logoTarget: LogoTarget = 'app'
) => {
  gs.registerPlugin(pathPlugin);

  const element = logo.getBoundingClientRect();

  const sidebarPlaceholders = document.getElementsByClassName('sidebar-placeholder');
  const sidebarPlaceholder = sidebarPlaceholders.length > 0 ? sidebarPlaceholders[0] : null;

  // Try to find the actual destination logo to get accurate target coordinates. The public header is always
  // mounted (hidden with opacity), so its box is measurable from here.
  const destinationLogo = document.querySelector(LOGO_TARGET_SELECTOR[logoTarget]) as SVGElement;
  const fallbackTarget = window.matchMedia('(min-width: 1025px)').matches ? DESKTOP_LOGO_TARGET : MOBILE_LOGO_TARGET;
  let targetX = fallbackTarget.x;
  let targetY = fallbackTarget.y;
  let targetScale = fallbackTarget.scale;

  if (destinationLogo) {
    const targetRect = destinationLogo.getBoundingClientRect();
    targetX = targetRect.left;
    targetY = targetRect.top;
    targetScale = targetRect.height / element.height;
  }

  // The animated SVG includes the mark to the left of the wordmark. The mark fades out, so offset the SVG itself
  // to align the remaining letters with the wordmark rendered in the app shell.
  const logoTargetX = targetX - FINAL_LETTER_OFFSET.x * targetScale;
  const logoTargetY = targetY - FINAL_LETTER_OFFSET.y * targetScale;

  if (element) {
    const letters = logo.querySelectorAll(':scope > path');
    const shape = logo.querySelector('g');

    panel.style.willChange = 'opacity, transform';
    logo.style.willChange = 'opacity, transform';

    gs.killTweensOf([logo, logoWrapper, panel, shape, ...letters]);

    const timeline = gs.timeline({
      onComplete: callback,
    });
    timeline.to(
      panel,
      {
        '--panel-y': '0px',
        '--panel-s': 0.9,
        duration: PANEL_EXIT_DURATION,
        ease: LOGO_ANIMATION_EASE,
        opacity: 0,
        onComplete: () => {
          panel.style.visibility = 'hidden';
        },
      },
      0
    );
    timeline.to(shape, { opacity: 0, duration: PANEL_EXIT_DURATION, ease: LOGO_ANIMATION_EASE }, 0);
    timeline.to(
      logo,
      {
        motionPath: {
          path: [
            {
              x: (logoTargetX - element.left) / 1.9,
              y: (logoTargetY - element.top) / 1.4,
            },
            {
              x: logoTargetX - element.left,
              y: logoTargetY - element.top,
            },
          ],
          curviness: 0.75,
        },
        duration: 1,
        ease: LOGO_ANIMATION_EASE,
      },
      0
    );
    timeline.to(logo, { rotate: '5deg', repeat: 1, yoyo: true, duration: 0.5 }, 0);
    timeline.to(logo, { scale: targetScale, duration: 0.9, ease: LOGO_ANIMATION_EASE }, 0);
    timeline.to(
      letters,
      {
        stagger: -0.08,
        '--logo-path-opacity': 1,
        '--logo-path-x': '-1px',
        '--logo-path-y': '7px',
        duration: 1,
      },
      0
    );

    // The placeholder stands in for the app sidebar, which onboarding does not have.
    if (sidebarPlaceholder && logoTarget === 'app') {
      timeline.to(sidebarPlaceholder, { x: '100%', opacity: 1, duration: 0.8 }, 0.2);
    }
  }
};
